// Phase 85.A.69 — Modal pop-up BẮT BUỘC khi admin đề xuất điểm trình
// (user choice Q11=b — không tắt được cho đến khi user accept/reject).
//
// Mount global (App.js). Logic:
//   • Poll `/api/athlete/rating-proposals` mỗi 30s + khi user login.
//   • Filter `admin_initiated=true` + `status=pending`.
//   • Nếu có ít nhất 1 → show modal.
//   • User accept → call PATCH /athlete/rating-proposals/{id} → close.
//   • User reject → cùng endpoint với decision="rejected" → close.
//   • KHÔNG có nút đóng modal — bắt buộc.
//
// Phân biệt với `RatingProposalBanner` (đã có sẵn) qua field `admin_initiated`:
//   • admin_initiated=true → modal bắt buộc (Q11=b).
//   • admin_initiated=false → banner thân thiện (existing).
//
// v17.perf.har3 (13/08/2026) — HAR PROD post-deploy cho thấy rating-proposals
// vẫn 8x/130s do modal này có polling ĐỘC LẬP 60s (raw setInterval) → double-
// fetch bám sát Gate poll. Migrate sang React Query observer-only, share
// `RATING_PROPOSALS_QUERY_KEY` với Gate — chỉ ĐỌC cache, filter admin_initiated.

import { useState } from "react";
import { m as motion, AnimatePresence } from "framer-motion";
import { CheckCircle, X, Star, ArrowUp, ArrowDown, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { RATING_PROPOSALS_QUERY_KEY } from "../../contexts/PendingRatingProposalGateContext";

export default function AdminProposalRequiredModal() {
  const { user } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [ack, setAck] = useState(false);
  const qc = useQueryClient();

  // v17.perf.har3 — Observer-only share cache với Gate. Gate là owner duy
  // nhất của polling network cho query key này (30s poll). Modal chỉ đọc
  // cache và filter admin_initiated proposal đầu tiên.
  const { data: proposalsRaw } = useQuery({
    queryKey: RATING_PROPOSALS_QUERY_KEY,
    enabled: false,             // Không tự trigger — Gate là owner.
    queryFn: async () => {
      // Placeholder (không được gọi thực tế do enabled:false).
      try {
        const r = await api.get("/athlete/rating-proposals");
        return r.data?.items || r.data || [];
      } catch {
        return [];
      }
    },
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: 0,
  });

  const list = Array.isArray(proposalsRaw) ? proposalsRaw : [];
  const proposal = list.find((p) => p?.admin_initiated && p?.status === "pending") || null;

  if (!user?.id || !proposal) return null;

  const cur = Number(proposal.current_rating || 0);
  const prop = Number(proposal.proposed_rating || 0);
  const diff = prop - cur;
  const isUp = diff > 0;

  // Sau khi user resolve (accept/reject/force-ack), invalidate cache để Gate
  // refetch lấy các proposal còn lại (nếu admin gửi nhiều).
  const removeFromCache = () => {
    qc.setQueryData(RATING_PROPOSALS_QUERY_KEY, (prev) =>
      Array.isArray(prev) ? prev.filter((p) => p.id !== proposal.id) : [],
    );
  };

  const handleDecide = async (decision) => {
    if (submitting) return;
    if (decision === "accepted" && !ack) {
      toast.error("Hãy tick xác nhận cam kết trước khi đồng ý");
      return;
    }
    setSubmitting(true);
    try {
      await api.patch(`/athlete/rating-proposals/${proposal.id}`, {
        decision,
        disclaimer_ack: ack || decision === "rejected",
      });
      toast.success(
        decision === "accepted"
          ? `Đã chấp nhận điểm trình mới: ${prop.toFixed(3)}`
          : "Đã từ chối đề xuất. Admin sẽ liên hệ nếu cần.",
      );
      removeFromCache();
      setAck(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Không gửi được phản hồi");
    } finally {
      setSubmitting(false);
    }
  };

  // Phase 85.A.172 — Force-ack mode: VĐV không có lựa chọn, chỉ bấm "Tôi đã hiểu".
  // Modal chặn ESC + outside click. Backend apply điểm BTC ngay.
  const isForceAck = !!proposal?.requires_force_ack;
  const handleForceAck = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const r = await api.post(`/athlete/rating-proposals/${proposal.id}/force-ack`);
      const applied = Number(r?.data?.applied_score || prop || 0);
      toast.success(`BTC đã áp dụng điểm trình ${applied.toFixed(3)} cho bạn.`);
      removeFromCache();
      setAck(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Không gửi được xác nhận");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[450] flex items-end sm:items-center justify-center bg-black/85 backdrop-blur-lg px-4 pb-4 sm:pb-0"
        data-testid="admin-proposal-required-modal"
      >
        <motion.div
          initial={{ y: 32, scale: 0.96, opacity: 0 }}
          animate={{ y: 0, scale: 1, opacity: 1 }}
          exit={{ y: 32, scale: 0.96, opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="w-full max-w-md rounded-3xl border border-amber-400/35 bg-gradient-to-br from-[#1A1208] via-[#0F1623] to-[#0A0F1C] shadow-[0_24px_80px_rgba(255,138,0,0.5)] overflow-hidden flex flex-col max-h-[92dvh]"
        >
          <div className="px-5 py-4 border-b border-white/8 bg-gradient-to-r from-amber-500/12 to-transparent">
            <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-amber-300/85 flex items-center gap-1.5">
              <Warning size={11} weight="fill" />
              {isForceAck ? "Quyết định cuối cùng từ BTC" : "Yêu cầu xác nhận"}
            </div>
            <div className="text-[16px] font-black text-white leading-tight mt-0.5">
              {isForceAck ? "BTC đã chốt điểm trình" : "Admin đề xuất điểm trình mới"}
            </div>
            <div className="text-[11.5px] text-white/55 mt-0.5">
              {isForceAck
                ? "Bạn đã đề xuất ngược lại, BTC đã xem xét và đưa ra quyết định cuối."
                : <>Đề xuất từ: <strong className="text-white/85">{proposal.proposed_by_name || "Admin"}</strong></>
              }
            </div>
          </div>

          <div className="px-5 py-5 space-y-4 overflow-y-auto">
            {isForceAck && (
              <div className="px-3 py-2.5 rounded-xl border border-amber-400/40 bg-amber-500/[0.08] text-amber-100/95 text-[12px] leading-relaxed">
                <div className="font-black text-[12.5px] mb-1">📢 Lưu ý quan trọng</div>
                Sau khi bạn đề xuất ngược, BTC đã đưa ra điểm cuối cùng. Bấm &quot;Tôi đã hiểu&quot; để áp dụng điểm trình này — bạn KHÔNG thể đề xuất tiếp.
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-2xl border border-white/8 bg-white/3 p-3.5 text-center">
                <div className="text-[10px] font-bold text-white/45 uppercase tracking-wide mb-1.5">
                  Điểm hiện tại
                </div>
                <div className="text-[28px] font-black text-white tabular-nums">
                  {cur.toFixed(3)}
                </div>
              </div>
              <div className="rounded-2xl border border-amber-400/40 bg-amber-500/10 p-3.5 text-center shadow-[0_4px_20px_rgba(255,138,0,0.2)]">
                <div className="text-[10px] font-bold text-amber-300 uppercase tracking-wide mb-1.5 flex items-center justify-center gap-1">
                  <Star size={10} weight="fill" />
                  {isForceAck ? "Điểm chốt" : "Đề xuất"}
                </div>
                <div className="text-[28px] font-black text-amber-200 tabular-nums">
                  {prop.toFixed(3)}
                </div>
                <div className={`text-[10.5px] font-bold mt-1 flex items-center justify-center gap-0.5 ${isUp ? "text-emerald-300" : "text-rose-300"}`}>
                  {isUp ? <ArrowUp size={10} weight="bold" /> : <ArrowDown size={10} weight="bold" />}
                  {isUp ? "+" : ""}{diff.toFixed(3)}
                </div>
              </div>
            </div>

            {proposal.reason && (
              <div className="rounded-xl border border-blue-400/25 bg-blue-500/[0.06] p-3">
                <div className="text-[10px] font-bold text-blue-300 uppercase tracking-wide mb-1">
                  Lý do
                </div>
                <div className="text-[12px] text-blue-100/85 leading-snug">
                  {proposal.reason}
                </div>
              </div>
            )}

            {!isForceAck && (
              <label className="flex items-start gap-2.5 px-3 py-2.5 rounded-xl border border-white/10 bg-white/3 cursor-pointer hover:bg-white/5 transition">
                <input
                  type="checkbox"
                  checked={ack}
                  onChange={(e) => setAck(e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-amber-500"
                  data-testid="admin-proposal-required-ack"
                />
                <span className="flex-1 text-[11.5px] text-white/75 leading-snug">
                  Tôi xác nhận điểm trình đề xuất là chính xác + cam kết chơi đúng trình độ trong các giải đấu.
                </span>
              </label>
            )}
          </div>

          <div className={`px-5 py-4 ${isForceAck ? "" : "grid grid-cols-2 gap-2.5"} border-t border-white/8 bg-black/30`}>
            {isForceAck ? (
              <button
                type="button"
                onClick={handleForceAck}
                disabled={submitting}
                className="w-full h-11 rounded-2xl bg-gradient-to-r from-[#FF8A00] to-[#FF9F1C] hover:shadow-[0_8px_24px_rgba(255,138,0,0.55)] text-white text-[13px] font-black transition active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="admin-proposal-required-force-ack"
              >
                <CheckCircle size={14} weight="fill" />
                {submitting ? "Đang áp dụng…" : "Tôi đã hiểu — Áp dụng điểm"}
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => handleDecide("rejected")}
                  disabled={submitting}
                  className="h-11 rounded-2xl border border-rose-400/30 bg-rose-500/8 hover:bg-rose-500/15 text-rose-200 text-[12.5px] font-bold transition flex items-center justify-center gap-1.5 disabled:opacity-50"
                  data-testid="admin-proposal-required-reject"
                >
                  <X size={13} weight="bold" />
                  Từ chối
                </button>
                <button
                  type="button"
                  onClick={() => handleDecide("accepted")}
                  disabled={submitting || !ack}
                  className="h-11 rounded-2xl bg-gradient-to-r from-[#FF8A00] to-[#FF9F1C] hover:shadow-[0_8px_24px_rgba(255,138,0,0.45)] text-white text-[12.5px] font-black transition active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-1.5"
                  data-testid="admin-proposal-required-accept"
                >
                  <CheckCircle size={13} weight="fill" />
                  Đồng ý
                </button>
              </>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
